// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Drop targets and drop-target banks (M1-17, PH-A23).
//
// A drop target is a face that stops being there. Its collider stays in the broadphase and
// simply stops answering, which costs one array read per candidate and avoids rebuilding
// the spatial grid mid-game.
//
// The **drop speed threshold** is what makes a bank feel mechanical rather than scripted.
// A real target needs a genuine hit to knock it down; a ball dribbling into one rattles off
// and leaves it standing, and every player has watched a weak shot fail to clear the last
// target of a bank. Without the threshold a bank completes itself on contact and the shot
// stops being a shot.

import type { Ball } from '../Ball'
import type { Collider } from '../colliders'
import { makeSegment } from '../colliders'
import type { MaterialName } from '../materials'
import type { World } from '../World'
import { Device, type DeviceState } from './Device'
import { EV_BANK_COMPLETE, EV_SWITCH_CLOSED, EV_TARGET_DOWN, EV_TARGET_UP } from '../events'

export interface DropTargetConfig {
  readonly id: string
  readonly a: readonly [number, number]
  readonly b: readonly [number, number]
  /**
   * Face normal, pointing into the play area — a target is struck from one side only.
   * Omit it to take the left-hand perpendicular of `a → b`, which is both harder to get
   * wrong than a hand-typed unit vector and validated the same way if supplied.
   */
  readonly normal?: readonly [number, number]
  /** Optional solid rear face. Rear contacts rebound without closing the scoring switch. */
  readonly solidBack?: boolean
  /** Normal approach speed below which the ball bounces off and the target stays up, m/s. */
  readonly dropSpeed?: number
  readonly material?: MaterialName
}

export class DropTarget extends Device {
  readonly kind = 'dropTarget'

  private readonly ax: number
  private readonly ay: number
  private readonly bx: number
  private readonly by: number
  private readonly normal: readonly [number, number] | undefined
  private readonly dropSpeed: number
  private readonly material: MaterialName

  /**
   * Whether any shot can knock this one over.
   *
   * A standup target is built as a `DropTarget` with an infinite `dropSpeed`, "which is exactly
   * what it is: a one-sided face wired to a switch". That is the right model and it threw away
   * the one thing the format had said — `kind` is `'standupTarget' | 'dropTarget'` and the
   * loader knew which it was building. Anything downstream that wants to tell them apart had no
   * way to: a renderer drawing a standup as a target that will fall, and a sound layer playing a
   * drop-target clack for a face that cannot drop.
   */
  readonly canDrop: boolean

  readonly solidBack: boolean
  private readonly faceNx: number
  private readonly faceNy: number

  /** Up and solid, or dropped and passable. */
  isUp = true

  /**
   * Step index at which `isUp` last changed, or −1 if it never has since the reset.
   *
   * The simulation drops a target between one step and the next, because a face that is half
   * there is a face that is neither solid nor passable and there is no honest answer for what
   * a ball hitting it should do. The mechanism takes about seventy milliseconds. This is the
   * only thing a renderer needs to draw the difference, and it is a stamp rather than a
   * countdown so nothing has to tick it down or be reset in two places.
   */
  movedAtStep = -1

  protected override ownState(): DeviceState {
    return { isUp: this.isUp, movedAtStep: this.movedAtStep }
  }

  protected override restoreOwn(state: DeviceState): void {
    this.isUp = state['isUp'] === true
    this.movedAtStep = Number(state['movedAtStep'])
  }

  constructor(config: DropTargetConfig) {
    super(config.id)
    this.ax = config.a[0]
    this.ay = config.a[1]
    this.bx = config.b[0]
    this.by = config.b[1]
    this.setOrigin((this.ax + this.bx) / 2, (this.ay + this.by) / 2)
    this.normal = config.normal
    this.dropSpeed = config.dropSpeed ?? 0.9
    this.canDrop = Number.isFinite(this.dropSpeed)
    this.material = config.material ?? 'plastic'
    this.solidBack = config.solidBack === true
    const face = makeSegment(this.ax, this.ay, this.bx, this.by, this.material, this.normal ? {normal:this.normal} : {})
    this.faceNx = face.nx; this.faceNy = face.ny
  }

  override colliders(): readonly Collider[] {
    const options: {
      oneSided: boolean
      device: number
      normal?: readonly [number, number]
    } = { oneSided: !this.solidBack, device: this.index }
    if (this.normal) options.normal = this.normal
    return [makeSegment(this.ax, this.ay, this.bx, this.by, this.material, options)]
  }

  override onColliderHit(
    world: World,
    ball: Ball,
    nx: number,
    ny: number,
    impactSpeed: number,
  ): void {
    if (!this.isUp) return
    // The solid back rebounds through the contact solver; only the active face closes a switch.
    // Suppress resting/zero-impulse callbacks rather than awarding repeated ghost contacts.
    if (this.solidBack && (nx*this.faceNx + ny*this.faceNy <= 0 || impactSpeed <= 0)) return
    world.events.emit(EV_SWITCH_CLOSED, this.index, ball.id, world.stepIndex, impactSpeed)
    if (impactSpeed < this.dropSpeed) return
    this.drop(world, ball.id)
  }

  drop(world: World, ballId: number): void {
    if (!this.isUp) return
    this.isUp = false
    this.movedAtStep = world.stepIndex
    this.collidersActive = false
    world.events.emit(EV_TARGET_DOWN, this.index, ballId, world.stepIndex, 0)
  }

  raise(world: World): void {
    if (this.isUp) return
    this.isUp = true
    this.movedAtStep = world.stepIndex
    this.collidersActive = true
    world.events.emit(EV_TARGET_UP, this.index, -1, world.stepIndex, 0)
  }

  override reset(): void {
    this.isUp = true
    this.movedAtStep = -1
    this.collidersActive = true
  }
}

export interface DropTargetBankConfig {
  readonly id: string
  readonly targets: readonly DropTarget[]
  /**
   * Steps between the last target dropping and the bank popping back up. Zero leaves the
   * bank down until the rule engine resets it, which is what a progressive bank wants.
   */
  readonly autoResetSteps?: number
}

/**
 * A bank groups targets so completion is a single event.
 *
 * The bank owns no geometry: it is bookkeeping over targets that already exist, which keeps
 * the rule engine from having to count switch closures itself.
 */
export class DropTargetBank extends Device {
  readonly kind = 'dropTargetBank'

  private readonly targets: readonly DropTarget[]
  private readonly autoResetSteps: number
  private resetAtStep = -1
  private wasComplete = false

  /** Completed cycles since the last reset. */
  completions = 0

  protected override ownState(): DeviceState {
    // The bank's own state only. Each target snapshots itself, because each is a device in the
    // world's own list — capturing them from here as well would restore them twice, and the
    // second write would be from a stale copy the moment the ordering changed.
    return { resetAtStep: this.resetAtStep, wasComplete: this.wasComplete, completions: this.completions }
  }

  protected override restoreOwn(state: DeviceState): void {
    this.resetAtStep = Number(state['resetAtStep'])
    this.wasComplete = state['wasComplete'] === true
    this.completions = Number(state['completions'])
  }

  constructor(config: DropTargetBankConfig) {
    super(config.id)
    if (config.targets.length === 0) {
      throw new Error(`DropTargetBank ${config.id}: needs at least one target`)
    }
    this.targets = config.targets
    this.autoResetSteps = config.autoResetSteps ?? 0
    let ox = 0
    let oy = 0
    for (const t of this.targets) {
      ox += t.originX
      oy += t.originY
    }
    this.setOrigin(ox / this.targets.length, oy / this.targets.length)
  }

  get downCount(): number {
    let n = 0
    for (const t of this.targets) if (!t.isUp) n++
    return n
  }

  get isComplete(): boolean {
    return this.downCount === this.targets.length
  }

  override step(world: World, _dt: number): void {
    const complete = this.isComplete

    if (complete && !this.wasComplete) {
      this.completions++
      world.events.emit(
        EV_BANK_COMPLETE,
        this.index,
        -1,
        world.stepIndex,
        this.targets.length,
      )
      this.resetAtStep = this.autoResetSteps > 0 ? world.stepIndex + this.autoResetSteps : -1
    }
    this.wasComplete = complete

    if (this.resetAtStep >= 0 && world.stepIndex >= this.resetAtStep) {
      this.resetAtStep = -1
      this.raiseAll(world)
    }
  }

  raiseAll(world: World): void {
    for (const t of this.targets) t.raise(world)
    this.wasComplete = false
  }

  override reset(): void {
    this.resetAtStep = -1
    this.wasComplete = false
    this.completions = 0
  }
}
